import React, { useRef, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { toasterMsg } from '../Toaster/Toaster';

const DEFAULT_ERROR_SHEET_FILENAME = 'error-sheet.xlsx';
const DOWNLOAD_CLEANUP_DELAY_MS = 100;
const TIMESTAMP_FORMAT_REGEX = /[:.]/g;
const TIMESTAMP_SLICE_END = -5;
const DOWNLOADED_ERROR_SHEETS_KEY = 'downloadedErrorSheets';
const DOWNLOADED_SHEETS_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function DuplicateConfirmModal({ show, onHide, onConfirm, duplicateCount, newCount, errorSheetUrl, isValidationError, validationMessage, data }) {
  const { t } = useTranslation('purchase-order');
  const isDownloadAttempted = useRef(false);

  useEffect(() => {
    if (!show) {
      isDownloadAttempted.current = false;
    }
  }, [show, duplicateCount, newCount, errorSheetUrl, isValidationError]);

  const getDownloadedSheets = useCallback(() => {
    try {
      const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
      if (!stored) return new Set();
      const storedData = JSON.parse(stored);
      const now = Date.now();
      const validEntries = storedData.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
      return new Set(validEntries.map((entry) => entry.url));
    } catch {
      return new Set();
    }
  }, []);

  const markSheetAsDownloaded = useCallback((url) => {
    const stored = localStorage.getItem(DOWNLOADED_ERROR_SHEETS_KEY);
    const markSheetData = stored ? JSON.parse(stored) : [];
    const now = Date.now();
    const validEntries = markSheetData.filter((entry) => now - entry.timestamp < DOWNLOADED_SHEETS_TTL_MS);
    validEntries.push({ url, timestamp: now });
    localStorage.setItem(DOWNLOADED_ERROR_SHEETS_KEY, JSON.stringify(validEntries));
  }, []);

  /**
   * Extracts filename from Content-Disposition header
   * @param {Headers} responseHeaders - Response headers object
   * @returns {string} Extracted filename or default filename
   */
  const extractFilenameFromHeaders = useCallback((responseHeaders) => {
    const contentDispositionHeader = responseHeaders.get('Content-Disposition');

    if (!contentDispositionHeader) {
      return DEFAULT_ERROR_SHEET_FILENAME;
    }

    const filenameMatch = contentDispositionHeader.match(/filename="?(.+?)"?$/);
    return filenameMatch ? filenameMatch[1] : DEFAULT_ERROR_SHEET_FILENAME;
  }, []);

  /**
   * Generates timestamped filename to prevent overwrites
   * @param {string} originalFilename - Original filename
   * @returns {string} Filename with timestamp appended
   */
  const generateTimestampedFilename = useCallback((originalFilename) => {
    const lastDotIndex = originalFilename.lastIndexOf('.');
    const fileNameWithoutExtension = lastDotIndex > 0 ? originalFilename.substring(0, lastDotIndex) : originalFilename;
    const fileExtension = lastDotIndex > 0 ? originalFilename.substring(lastDotIndex) : '.xlsx';
    const timestamp = new Date().toISOString().replace(TIMESTAMP_FORMAT_REGEX, '-').slice(0, TIMESTAMP_SLICE_END);

    return `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
  }, []);

  /**
   * Triggers browser download for blob data
   * @param {Blob} blobData - The blob data to download
   * @param {string} downloadFilename - Filename for the download
   */
  const triggerBlobDownload = useCallback((blobData, downloadFilename) => {
    const blobUrl = window.URL.createObjectURL(blobData);
    const downloadLink = document.createElement('a');

    downloadLink.href = blobUrl;
    downloadLink.download = downloadFilename;
    downloadLink.style.display = 'none';

    document.body.appendChild(downloadLink);
    downloadLink.click();

    setTimeout(() => {
      if (document.body.contains(downloadLink)) {
        document.body.removeChild(downloadLink);
      }
      window.URL.revokeObjectURL(blobUrl);
    }, DOWNLOAD_CLEANUP_DELAY_MS);
  }, []);

  const downloadErrorSheet = useCallback(async () => {
    if (!errorSheetUrl || isDownloadAttempted.current) {
      return;
    }

    const downloadedSheets = getDownloadedSheets();
    if (downloadedSheets.has(errorSheetUrl)) {
      console.log('Error sheet already downloaded in another tab/device, skipping download');
      return;
    }

    isDownloadAttempted.current = true;

    try {
      const response = await fetch(errorSheetUrl, {
        method: 'GET',
        mode: 'cors',
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log('Error sheet not found (likely moved/deleted by another tab/device), skipping download');
          markSheetAsDownloaded(errorSheetUrl);
          return;
        }
        throw new Error(`Failed to fetch error sheet: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const extractedFilename = extractFilenameFromHeaders(response.headers);
      const timestampedFilename = generateTimestampedFilename(extractedFilename);

      triggerBlobDownload(blob, timestampedFilename);
      markSheetAsDownloaded(errorSheetUrl);
    } catch (error) {
      if (error?.message?.includes('404') || error?.message?.includes('Failed to fetch')) {
        console.log('Error sheet not available (likely moved/deleted by another tab/device), skipping download');
        markSheetAsDownloaded(errorSheetUrl);
        return;
      }
      console.error('Error downloading error sheet:', error);
      const errorMessage = error?.message || 'Failed to download error sheet. Please try again.';
      toasterMsg(errorMessage, 'danger');
    } finally {
      isDownloadAttempted.current = false;
    }
  }, [errorSheetUrl, extractFilenameFromHeaders, generateTimestampedFilename, triggerBlobDownload, getDownloadedSheets, markSheetAsDownloaded]);

  const handleCancel = useCallback(async () => {
    if (errorSheetUrl) {
      try {
        await downloadErrorSheet();
      } catch (error) {
        console.error('Download failed in handleCancel:', error);
      }
    }
    onHide();
  }, [downloadErrorSheet, onHide, errorSheetUrl]);

  const handleConfirm = useCallback(async () => {
    if (errorSheetUrl && isValidationError) {
      try {
        await downloadErrorSheet();
      } catch (error) {
        console.error('Download failed in handleConfirm:', error);
      }
    }
    onConfirm();
  }, [downloadErrorSheet, onConfirm, errorSheetUrl, isValidationError]);

  return (
    <Modal show={show} backdrop="static" className="common-custom-modal modal-medium" aria-labelledby="contained-modal-title-vcenter" onHide={handleCancel}>
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          <span>{t('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.DUPLICATES_FOUND_TITLE')}</span>
        </Modal.Title>
        <button type="button" aria-label="close" onClick={handleCancel} className="btn">
          <i className="icon-close cursor-pointer" />
        </button>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-inner-data">
          <span>
            {isValidationError ? (
              <>
                <p className="mb-16 text-center">{validationMessage || t('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.VALIDATION_FAILED')}</p>
                {errorSheetUrl && (
                  <p className="mb-16 text-center">
                    {t('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.DOWNLOAD_ERROR_SHEET_PROMPT') ||
                      'The error sheet will be downloaded when you close this dialog.'}
                  </p>
                )}
              </>
            ) : (
              <p className="mb-16 text-center">
                {data?.message ||
                  t('BACKEND_SERVER.CONTROLLER.PRODUCT_PURCHASE_ORDERS.DUPLICATES_FOUND_MESSAGE', {
                    duplicateCount,
                    newCount,
                  })}
              </p>
            )}
          </span>
        </div>
        <div className="common-model-footer">
          <button type="button" aria-label="confirm" onClick={handleConfirm} className="btn p-0">
            <span className="icon-Tick-1 f-24" />
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

DuplicateConfirmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  duplicateCount: PropTypes.number,
  newCount: PropTypes.number,
  errorSheetUrl: PropTypes.string,
  isValidationError: PropTypes.bool,
  validationMessage: PropTypes.string,
  data: PropTypes.shape({
    message: PropTypes.string,
  }),
};

DuplicateConfirmModal.defaultProps = {
  duplicateCount: 0,
  newCount: 0,
  errorSheetUrl: null,
  isValidationError: false,
  validationMessage: null,
  data: null,
};

export default DuplicateConfirmModal;
